import { useState, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { elexity } from '../../utilities/AdvancedGraphHelper';
import AuthenticatedComponent from '../../utilities/AuthenticatedComponent';
import configuration from '../../config';

import { useCampusControlModes } from '../../context/CampusContextProvider';
import { useCharts } from './useCharts';
import { CONTROL_MODE, CONTROL_MODE_CATEGORY } from '../../config/control';

const BASE_GRAPH_URL = `${configuration['backend_host']}/admin/monitor/{entityKey}/thermalZone/graphData`;

export const PerformanceChart = ({ entityKey, subtitle, graphConfiguration, className }) => {
  const [chartTabActive, setChartTabActive] = useState('live');
  const historyChartRef = useRef(null);
  const liveChartRef = useRef(null);
  const initialized = useRef(false);
  const controlModesRef = useRef([]);

  const { applyStrategyModes } = useCharts();

  const graphConfigurationWithStrategyModes = {
    ...graphConfiguration,
    livePrimaryYAxisConfig: {
      top: '25%',
      height: '75%',
      offset: 0,
    },

    liveXAxes: [{ linkedTo: 0, visible: false }],

    liveYAxes: [
      {
        title: { text: 'Strategies' },
        categories: [],
        top: '0%',
        height: '20%',
        offset: 10,
        opposite: false,
        min: -0.5,
        max: 0.5,
        labels: { enabled: true, style: { fontSize: '9px', color: '#6b7280' } },
      },
    ],

    onLiveGraphReady: (liveChart) => {
      applyStrategyModes(liveChart, controlModesRef.current);
      let debounceTimer;
      liveChart.xAxis[0].update(
        {
          events: {
            afterSetExtremes: () => {
              clearTimeout(debounceTimer);
              debounceTimer = setTimeout(() => {
                applyStrategyModes(liveChart, controlModesRef.current);
              }, 150);
            },
          },
        },
        false
      );
    },
  };

  const controlModes = useCampusControlModes() || [];

  const historyDivId = `${entityKey}-historyPerformanceGraph`;
  const liveDivId = `${entityKey}-livePerformanceGraph`;

  function handleRefresh() {
    elexity.admin.monitor.graphs.triggerHistoryGraphReload();
  }

  useEffect(() => {
    controlModesRef.current = controlModes;
    applyStrategyModes(elexity.admin.monitor.graphs.performanceLiveGraph, controlModes);
  }, [controlModes]);

  useEffect(() => {
    if (subtitle && !initialized.current && historyChartRef.current && liveChartRef.current) {
      initialized.current = true;
      const authenticatedComponent = new AuthenticatedComponent();
      elexity.admin.monitor.graphs.initAuth(authenticatedComponent.generateAuthenticatedHeader());
      elexity.admin.monitor.graphs.init(
        entityKey,
        `${BASE_GRAPH_URL}/definition`,
        `${BASE_GRAPH_URL}/history`,
        `${BASE_GRAPH_URL}/live`,
        graphConfigurationWithStrategyModes,
        historyDivId,
        liveDivId,
        subtitle
      );
    }
  }, [subtitle]);

  return (
    <ChartContainer className={`chart ${className || ''}`}>
      <div className="chart-nav">
        <div className="tabs">
          <div
            className={chartTabActive === 'live' ? 'live active' : 'live'}
            onClick={() => setChartTabActive('live')}
          >
            Live
          </div>
          <div
            className={chartTabActive === 'history' ? 'history active' : 'history'}
            onClick={() => setChartTabActive('history')}
          >
            History
          </div>
        </div>
        {chartTabActive === 'history' && (
          <div className="refresh" onClick={handleRefresh}>
            Refresh Graph
          </div>
        )}
      </div>
      <div className="active-chart-container">
        <div
          id={historyDivId}
          className={chartTabActive === 'history' ? 'performanceGraph' : 'hidden performanceGraph'}
          ref={historyChartRef}
        />
        <div
          id={liveDivId}
          className={chartTabActive === 'live' ? 'performanceGraph' : 'hidden performanceGraph'}
          ref={liveChartRef}
        />
      </div>
    </ChartContainer>
  );
};

const ChartContainer = styled.div`
  height: 100%;

  .active-chart-container {
    background: white;
    border-radius: 0 12px 12px 12px;
    box-shadow: 0px 2px 6px #02222b14;
    padding: 6px;

    .performanceGraph {
      height: 600px;
    }

    .hidden {
      display: none;
      height: 0;
    }
  }
`;
